/**
 * Offline mutation log and reconciliation.
 *
 * A tech in a crawlspace has no signal. They tap "Started", type a note, shoot
 * three photos, tap "Completed", and collect a signature. All of that lands in
 * a local queue. Ten minutes later the truck rolls onto a road with LTE and the
 * queue flushes.
 *
 * Two rules govern this file:
 *
 *   1. Nothing is dropped silently. Every queued mutation ends in exactly one
 *      of five explicit outcomes, and an entry the server said nothing about
 *      STAYS in the queue. There is no code path that removes an entry without
 *      recording why.
 *
 *   2. The batch is validated against a projection, not against the state the
 *      server happened to be in when the batch arrived. `scheduled ->
 *      in_progress -> completed` queued offline is a legal pair, and checking
 *      both against the stored `scheduled` would wrongly reject the second.
 *
 * Both the server (`POST /sync/push`) and the mobile client run this. The
 * server decides; the client interprets the decisions.
 */
import { checkTransition, isJobStatus, JOB_STATUS_LABELS, } from './job-status.js';
/**
 * How far ahead a phone caches.
 *
 * Fourteen days is a technician's real horizon: far enough that the calendar
 * works in a basement for anything they are plausibly being asked about, near
 * enough that the pull stays small with a hundred phones doing it. Picking a
 * date beyond it is legal — the app fetches it and says so if there is no
 * signal — because a date nobody can reach is worse than a slow one.
 *
 * It lives here rather than in either app so the server's default and the
 * phone's request cannot drift apart and leave a day silently uncached.
 */
export const FIELD_CACHE_DAYS = 14;
/** The furthest a single pull may reach. Past this, use the jobs endpoint. */
export const FIELD_CACHE_MAX_DAYS = 30;
/**
 * How far *back* a phone caches.
 *
 * A technician is asked about last week's work more often than anyone designing
 * a scheduling app expects: what did I do at that address, did I take payment,
 * what did I write on the note. Caching only forwards made the calendar able to
 * show a day three weeks out and unable to show yesterday, which is the wrong
 * way round.
 *
 * A week is enough for "the job I did on Tuesday" and short enough that the
 * pull stays small. Older than that is still reachable — the day picker fetches
 * it — just not offline.
 */
export const FIELD_CACHE_BACK_DAYS = 7;
export const MUTATION_KINDS = [
    'job_status',
    'job_en_route',
    'job_note',
    'job_attachment',
    'job_signature',
    'job_payment',
];
/** Additive mutations never conflict with each other — only ordering matters. */
export const ADDITIVE_KINDS = [
    'job_note',
    'job_attachment',
    'job_signature',
    'job_payment',
    'job_en_route',
];
/** Progression rank. Used only to tell "server is ahead" from "genuine clash". */
const STATUS_RANK = {
    unscheduled: 0,
    scheduled: 1,
    in_progress: 2,
    completed: 3,
    invoiced: 4,
    paid: 5,
    cancelled: -1,
};
/**
 * Canonical execution order: the moment the tech tapped, then device sequence.
 * Never the order the entries arrived in — a retry can reorder the wire.
 */
export function orderMutations(entries) {
    return [...entries].sort((a, b) => {
        const ta = Date.parse(a.createdAt);
        const tb = Date.parse(b.createdAt);
        if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb)
            return ta - tb;
        return a.seq - b.seq;
    });
}
function contextFor(job) {
    return {
        assignedTechId: job.assignedTechId,
        scheduledStart: job.scheduledStart,
        scheduledEnd: job.scheduledEnd,
        hasInvoice: job.hasInvoice,
        invoiceBalanceCents: job.invoiceBalanceCents,
    };
}
/**
 * Decide what to do with every entry in a flush. Pure: no I/O, no clock.
 * The caller executes `toApply` in the order given, inside one transaction.
 */
export function reconcileMutations(entries, state) {
    const applied = new Set(state.appliedMutationIds);
    // Deep-ish copy so the caller's state object is never mutated.
    const projected = {};
    for (const [id, job] of Object.entries(state.jobs))
        projected[id] = { ...job };
    const outcomes = [];
    const toApply = [];
    /** Guards against the same id appearing twice within one batch. */
    const seenInBatch = new Set();
    for (const entry of orderMutations(entries)) {
        const base = { id: entry.id, seq: entry.seq, jobId: entry.jobId, kind: entry.kind };
        if (!entry.id || typeof entry.id !== 'string') {
            outcomes.push({ ...base, decision: 'reject', reason: 'Mutation is missing an id' });
            continue;
        }
        if (applied.has(entry.id) || seenInBatch.has(entry.id)) {
            outcomes.push({ ...base, decision: 'duplicate', reason: 'Already recorded' });
            continue;
        }
        if (!MUTATION_KINDS.includes(entry.kind)) {
            outcomes.push({ ...base, decision: 'reject', reason: `Unknown change type "${entry.kind}"` });
            continue;
        }
        const job = projected[entry.jobId];
        if (!job) {
            outcomes.push({
                ...base,
                decision: 'reject',
                reason: 'This job no longer exists or is not assigned to you',
            });
            continue;
        }
        if (entry.kind === 'job_status') {
            const target = entry.payload['status'];
            if (!isJobStatus(target)) {
                outcomes.push({ ...base, decision: 'reject', reason: `Invalid status "${String(target)}"` });
                continue;
            }
            if (job.status === target) {
                outcomes.push({
                    ...base,
                    decision: 'superseded',
                    reason: `Job is already ${JOB_STATUS_LABELS[target]}`,
                    resultingStatus: job.status,
                });
                seenInBatch.add(entry.id);
                continue;
            }
            const check = checkTransition(job.status, target, contextFor(job));
            if (check.ok) {
                job.status = target;
                job.version = (job.version ?? 0) + 1;
                toApply.push(entry);
                seenInBatch.add(entry.id);
                outcomes.push({ ...base, decision: 'apply', resultingStatus: target });
                continue;
            }
            // Illegal from here. Is that because the office already pushed the job
            // further along (harmless), or because the two genuinely disagree?
            const serverAhead = job.status !== 'cancelled' && STATUS_RANK[target] < STATUS_RANK[job.status];
            if (serverAhead) {
                outcomes.push({
                    ...base,
                    decision: 'superseded',
                    reason: `Job has already moved on to ${JOB_STATUS_LABELS[job.status]}`,
                    resultingStatus: job.status,
                });
                seenInBatch.add(entry.id);
                continue;
            }
            outcomes.push({
                ...base,
                decision: 'conflict',
                reason: check.reason,
                resultingStatus: job.status,
            });
            // Deliberately NOT added to seenInBatch: a later identical entry should
            // get the same conflict verdict rather than being masked as a duplicate.
            continue;
        }
        if (entry.kind === 'job_en_route') {
            if (job.status === 'cancelled') {
                outcomes.push({ ...base, decision: 'superseded', reason: 'Job was cancelled' });
                seenInBatch.add(entry.id);
                continue;
            }
            if (job.enRouteAt) {
                outcomes.push({ ...base, decision: 'superseded', reason: 'Already marked en route' });
                seenInBatch.add(entry.id);
                continue;
            }
            job.enRouteAt = entry.createdAt;
            toApply.push(entry);
            seenInBatch.add(entry.id);
            outcomes.push({ ...base, decision: 'apply', resultingStatus: job.status });
            continue;
        }
        // Additive: notes, photos, signatures, payments. These attach to the job
        // regardless of status; a note written about a completed job is still true.
        if (job.status === 'cancelled') {
            outcomes.push({
                ...base,
                decision: 'conflict',
                reason: 'Job was cancelled while you were offline',
                resultingStatus: job.status,
            });
            continue;
        }
        toApply.push(entry);
        seenInBatch.add(entry.id);
        outcomes.push({ ...base, decision: 'apply', resultingStatus: job.status });
    }
    return { outcomes, toApply, projectedJobs: projected };
}
/**
 * Fold a server response back into the local queue.
 *
 * The invariant, asserted by the test suite: for every input entry, it is
 * either in `remaining` or accounted for by an outcome in
 * `settled | rejected`. An entry can never simply vanish.
 */
export function applyOutcomes(queue, outcomes) {
    const byId = new Map(outcomes.map((o) => [o.id, o]));
    const remaining = [];
    const settled = [];
    const conflicts = [];
    const rejected = [];
    const unacknowledged = [];
    for (const entry of queue) {
        const outcome = byId.get(entry.id);
        if (!outcome) {
            // The server never mentioned it. Perhaps the response was truncated, or
            // the batch was split. Keep it. This is the "never drop" branch.
            unacknowledged.push(entry);
            remaining.push(entry);
            continue;
        }
        switch (outcome.decision) {
            case 'apply':
            case 'duplicate':
            case 'superseded':
                settled.push(outcome);
                break;
            case 'reject':
                rejected.push(outcome);
                break;
            case 'conflict':
                conflicts.push(outcome);
                remaining.push({
                    ...entry,
                    blocked: true,
                    blockedReason: outcome.reason ?? 'Conflicts with the office',
                    attempts: (entry.attempts ?? 0) + 1,
                });
                break;
        }
    }
    return { remaining, settled, conflicts, rejected, unacknowledged };
}
/** Entries eligible to be sent right now — blocked ones are held back. */
export function sendableMutations(queue) {
    return orderMutations(queue.filter((m) => !m.blocked));
}
/** Drives the "3 pending" pill in the mobile header. */
export function pendingSummary(queue) {
    const byKind = {};
    let oldest = null;
    for (const m of queue) {
        byKind[m.kind] = (byKind[m.kind] ?? 0) + 1;
        if (!oldest || Date.parse(m.createdAt) < Date.parse(oldest))
            oldest = m.createdAt;
    }
    return {
        total: queue.length,
        sendable: queue.filter((m) => !m.blocked).length,
        blocked: queue.filter((m) => m.blocked).length,
        oldestCreatedAt: oldest,
        byKind,
    };
}
/**
 * Apply the queue on top of cached jobs so the tech sees their own taps
 * immediately, offline, without waiting for the server to agree.
 */
export function projectLocally(jobs, queue) {
    const pending = new Map();
    const overlay = new Map();
    for (const m of orderMutations(queue)) {
        pending.set(m.jobId, (pending.get(m.jobId) ?? 0) + 1);
        if (m.blocked)
            continue;
        const current = overlay.get(m.jobId) ?? {};
        if (m.kind === 'job_status' && isJobStatus(m.payload['status'])) {
            current.status = m.payload['status'];
        }
        if (m.kind === 'job_en_route')
            current.enRouteAt = m.createdAt;
        overlay.set(m.jobId, current);
    }
    return jobs.map((j) => {
        const o = overlay.get(j.id);
        return {
            ...j,
            ...(o?.status ? { status: o.status } : {}),
            ...(o?.enRouteAt ? { enRouteAt: o.enRouteAt } : {}),
            pendingCount: pending.get(j.id) ?? 0,
        };
    });
}
/** Stable id for a mutation without pulling in a uuid dependency on device. */
export function makeMutationId() {
    const g = globalThis;
    if (g.crypto?.randomUUID)
        return g.crypto.randomUUID();
    return `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}
//# sourceMappingURL=sync.js.map